import type {
    FC
} from "react";
import {
    View
} from "react-native";
import type IQuestionnaireReviewProps from "./type";
import stylesheet, {
    getQuestionnaireReviewRowStyle,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import type {
    QuestionnairePartContentProps,
    IQuestionnaireItem
} from "../../type";
import {
    Pencil as PencilIcon
} from "lucide-react-native";
import {
    isolateText
} from "../../../../helpers/locale";
import {
    withQuestionnaireContext
} from "../../context";
import {
    getQuestionnaireReviewText
} from "../../util";
import QuestionnaireSwap from "../../internal/swap";
import Button from "../../../button";
import Alert from "../../../alert";
import Card from "../../../card";
import Text from "../../../text";

const QuestionnaireEditIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => {
    return <PencilIcon
        color={customColor}
        size={size}
    />;
};

const QuestionnaireReviewContent: FC<QuestionnairePartContentProps<IQuestionnaireReviewProps>> = ({
    customTheme: customThemeProp,
    context,
    style,
    ...props
}) => {
    const customTheme = customThemeProp ?? context.customTheme;

    const {
        controlHeights,
        iconSizes,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(context.customLocalize);

    const {
        container: containerDynamicStyle,
        action: actionDynamicStyle
    } = useStyles({
        controlHeights,
        iconSizes,
        spaces
    });

    const isEditable = !context.isSubmitted;

    const renderRow = (item: IQuestionnaireItem, index: number) => {
        const label = item.label ?? item.title;

        const text = getQuestionnaireReviewText({
            answer: context.answers[item.name],
            item
        });

        return <View
            style={[
                stylesheet.row,
                getQuestionnaireReviewRowStyle({
                    isLast: index === context.items.length - 1,
                    isFirst: index === 0,
                    borders,
                    colors,
                    spaces
                })
            ]}
            key={item.name}
            role="listitem"
        >
            <View
                style={stylesheet.text}
            >
                <Text
                    customTheme={customTheme}
                    variant="bodySmallSize"
                    color="mid"
                >
                    {label}
                </Text>
                <Text
                    color={text ? "high" : "mid"}
                    customTheme={customTheme}
                    variant="bodyMediumSize"
                >
                    {text ? isolateText({
                        text
                    }) : localize("questionnaire-skipped")}
                </Text>
            </View>
            {
                isEditable ?
                    <Button
                        accessibilityLabel={localize("questionnaire-edit-answer", [
                            label
                        ])}
                        onPress={() => {
                            context.handleEdit(index);
                        }}
                        isDisabled={context.isLoading}
                        icon={QuestionnaireEditIcon}
                        style={actionDynamicStyle}
                        customTheme={customTheme}
                        spreadBehaviour="free"
                        variant="ghost"
                        type="neutral"
                        size="small"
                    />
                :
                    null
            }
        </View>;
    };

    const renderTitle = () => {
        if(!isEditable) {
            return null;
        }

        return <View
            ref={context.contentRef}
            tabIndex={-1}
        >
            <Text
                variant="titleMediumSize"
                customTheme={customTheme}
                color="high"
            >
                {localize("questionnaire-review-title")}
            </Text>
        </View>;
    };

    const renderAlert = () => {
        if(!context.isSubmitFailed || !isEditable) {
            return null;
        }

        return <Alert
            title={context.submitErrorText ?? localize("questionnaire-submit-failed")}
            customLocalize={context.customLocalize}
            customTheme={customTheme}
            type="danger"
        />;
    };

    return <Card.Content>
        <QuestionnaireSwap
            key={context.activeIndex}
            customTheme={customTheme}
            step={context.swapStep}
        >
            <View
                {...props}
                style={[
                    style,
                    stylesheet.container,
                    containerDynamicStyle
                ]}
            >
                {renderTitle()}
                {renderAlert()}
                <View
                    style={stylesheet.list}
                    role="list"
                >
                    {context.items.map(renderRow)}
                </View>
            </View>
        </QuestionnaireSwap>
    </Card.Content>;
};
export default withQuestionnaireContext("Review", QuestionnaireReviewContent);
